import { createTheme } from '@mui/material/styles';

// Brand Colors
const BRAND = {
  primary: '#005595',      // Medium Electric Blue - Main actions
  secondary: '#99C1E7',    // Light Blue - Secondary actions
  accent: '#F7931E',       // Sun Orange - Highlights
};

// Status Colors
const STATUS = {
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#0EA5E9',
};

// Common colors
const COMMON = {
  white: '#FFFFFF',
  black: '#000000',
};

/**
 * Light Theme Palette
 */
const lightPalette = {
  mode: 'light',
  primary: {
    main: BRAND.primary,
    light: '#3383CF',
    dark: '#004477',
    contrastText: COMMON.white,
  },
  secondary: {
    main: BRAND.secondary,
    light: '#CCE0F3',
    dark: '#66A2DB',
    contrastText: '#00335A',
  },
  accent: {
    main: BRAND.accent,
    light: '#FFA733',
    dark: '#E07A0A',
    contrastText: COMMON.white,
  },
  success: {
    main: STATUS.success,
    light: '#B8E8D0',
    dark: '#16A34A',
  },
  warning: {
    main: STATUS.warning,
    light: '#FFD699',
    dark: '#D97706',
  },
  error: {
    main: STATUS.error,
    light: '#FCA5A5',
    dark: '#DC2626',
  },
  info: {
    main: STATUS.info,
    light: '#7DD3FC',
    dark: '#0284C7',
  },
  text: {
    primary: 'rgba(26, 26, 26, 0.87)',
    secondary: 'rgba(102, 102, 102, 0.60)',
    disabled: 'rgba(153, 153, 153, 0.38)',
  },
  background: {
    default: '#F8FAFC',
    paper: COMMON.white,
  },
  divider: 'rgba(0, 0, 0, 0.08)',
};

/**
 * Dark Theme Palette
 */
const darkPalette = {
  mode: 'dark',
  primary: {
    main: BRAND.primary,
    light: '#3383CF',
    dark: '#004477',
    contrastText: COMMON.white,
  },
  secondary: {
    main: BRAND.secondary,
    light: '#CCE0F3',
    dark: '#66A2DB',
    contrastText: '#00335A',
  },
  accent: {
    main: BRAND.accent,
    light: '#FFA733',
    dark: '#E07A0A',
    contrastText: COMMON.white,
  },
  success: {
    main: '#4ADE80',
    light: '#86EFAC',
    dark: '#22C55E',
  },
  warning: {
    main: '#FBBF24',
    light: '#FCD34D',
    dark: '#F59E0B',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
  },
  info: {
    main: '#38BDF8',
    light: '#7DD3FC',
    dark: '#0284C7',
  },
  text: {
    primary: 'rgba(255, 255, 255, 0.87)',
    secondary: 'rgba(255, 255, 255, 0.60)',
    disabled: 'rgba(255, 255, 255, 0.38)',
  },
  background: {
    default: '#0B1120',
    paper: '#111827',
  },
  divider: 'rgba(255, 255, 255, 0.08)',
};

/**
 * Typography Configuration
 */
const typography = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontFamilyMono: '"JetBrains Mono", monospace',
  htmlFontSize: 16,
  fontSize: 14,
  h1: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 600,
    fontSize: '2rem',      // 32px
    lineHeight: 2.5,       // 40px
    letterSpacing: 0,
  },
  h2: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 600,
    fontSize: '1.5rem',    // 24px
    lineHeight: 2,         // 32px
    letterSpacing: 0,
  },
  h3: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 600,
    fontSize: '1.25rem',   // 20px
    lineHeight: 1.75,      // 28px
    letterSpacing: 0,
  },
  h4: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 600,
    fontSize: '1.125rem',  // 18px
    lineHeight: 1.8,       // 26px
    letterSpacing: 0,
  },
  h5: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 600,
    fontSize: '1rem',      // 16px
    lineHeight: 1.5,       // 24px
    letterSpacing: 0,
  },
  h6: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 600,
    fontSize: '0.875rem',  // 14px
    lineHeight: 1.43,      // 20px
    letterSpacing: 0,
  },
  subtitle1: {
    fontSize: '1rem',
    lineHeight: 1.5,
    fontWeight: 400,
  },
  subtitle2: {
    fontSize: '0.875rem',
    lineHeight: 1.57,
    fontWeight: 500,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.5,
    fontWeight: 400,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.43,
    fontWeight: 400,
  },
  button: {
    fontSize: '0.875rem',  // 14px
    lineHeight: 1,         // 14px
    fontWeight: 500,
    textTransform: 'none', // Modern flat design
  },
  caption: {
    fontSize: '0.75rem',   // 12px
    lineHeight: 1.33,      // 16px
    fontWeight: 400,
  },
  overline: {
    fontSize: '0.75rem',
    lineHeight: 2,         // 16px
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.75px',
  },
};

/**
 * Component Style Overrides
 */
const components = {
  // Button Styles
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '10px 20px',
        fontWeight: 500,
        fontSize: '0.875rem',
        lineHeight: 1,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      contained: {
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        '&:hover': {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
      sizeSmall: {
        padding: '6px 12px',
        fontSize: '0.75rem',
      },
      sizeLarge: {
        padding: '14px 28px',
        fontSize: '1rem',
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
};

/**
 * Light Theme Component Overrides
 */
const lightComponents = {
  ...components,
  // TextField Styles
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
          },
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(0, 0, 0, 0.23)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2,
        },
      },
    },
  },
  // Card Styles
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '24px',
        '&:last-child': {
          paddingBottom: 24,
        },
      },
    },
  },
  // Paper Styles
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        backgroundImage: 'none',
      },
      elevation1: {
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      },
      elevation2: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  // Chip Styles
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontWeight: 500,
      },
    },
  },
  // Alert Styles
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  // Dialog Styles
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '24px 24px 16px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px 24px',
      },
    },
  },
  // Table Styles
  MuiTableContainer: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '16px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      },
      head: {
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
  },
  // Drawer Styles
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
      },
    },
  },
  // AppBar Styles
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      },
    },
  },
  // List Styles
  MuiListItem: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        margin: '4px 8px',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        minHeight: 48,
      },
    },
  },
  // Badge Styles
  MuiBadge: {
    styleOverrides: {
      badge: {
        fontWeight: 600,
      },
    },
  },
  // Tooltip Styles
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 6,
        fontSize: '0.75rem',
        padding: '6px 12px',
      },
    },
  },
  // Snackbar Styles
  MuiSnackbarContent: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  // LinearProgress Styles
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        height: 6,
      },
    },
  },
  // Skeleton Styles
  MuiSkeleton: {
    styleOverrides: {
      root: {
        borderRadius: 4,
      },
    },
  },
  // Autocomplete Styles
  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        borderRadius: 8,
      },
    },
  },
  // Select Styles
  MuiSelect: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#F5F5F5',
        },
      },
      icon: {
        fontSize: '1.25rem',
        color: 'rgba(0, 0, 0, 0.54)',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        color: 'rgba(26, 26, 26, 0.87)',
        '&:hover': {
          backgroundColor: 'rgba(0, 85, 149, 0.08)',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(0, 85, 149, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(0, 85, 149, 0.18)',
          },
        },
      },
    },
  },
  // Checkbox Styles
  MuiCheckbox: {
    styleOverrides: {
      root: {
        borderRadius: 4,
      },
    },
  },
  // Switch Styles
  MuiSwitch: {
    styleOverrides: {
      root: {
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: BRAND.primary,
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
          backgroundColor: BRAND.primary,
        },
      },
    },
  },
};

/**
 * Dark Theme Component Overrides
 */
const darkComponents = {
  ...components,
  // TextField Styles
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.23)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: BRAND.primary,
          },
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255, 255, 255, 0.23)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2,
          borderColor: BRAND.primary,
        },
      },
    },
  },
  // Card Styles
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '24px',
        '&:last-child': {
          paddingBottom: 24,
        },
      },
    },
  },
  // Paper Styles
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        backgroundImage: 'none',
        backgroundColor: darkPalette.background.paper,
      },
      elevation1: {
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
      },
      elevation2: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      },
    },
  },
  // Chip Styles
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontWeight: 500,
      },
    },
  },
  // Alert Styles
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  // Dialog Styles
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '24px 24px 16px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px 24px',
      },
    },
  },
  // Table Styles
  MuiTableContainer: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '16px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
      },
      head: {
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
  },
  // Drawer Styles
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
      },
    },
  },
  // AppBar Styles
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
      },
    },
  },
  // List Styles
  MuiListItem: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        margin: '4px 8px',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        minHeight: 48,
      },
    },
  },
  // Badge Styles
  MuiBadge: {
    styleOverrides: {
      badge: {
        fontWeight: 600,
      },
    },
  },
  // Tooltip Styles
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 6,
        fontSize: '0.75rem',
        padding: '6px 12px',
      },
    },
  },
  // Snackbar Styles
  MuiSnackbarContent: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  // LinearProgress Styles
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        height: 6,
      },
    },
  },
  // Skeleton Styles
  MuiSkeleton: {
    styleOverrides: {
      root: {
        borderRadius: 4,
      },
    },
  },
  // Autocomplete Styles
  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        borderRadius: 8,
      },
    },
  },
  // Select Styles
  MuiSelect: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#F5F5F5',
        },
      },
      icon: {
        fontSize: '1.25rem',
        color: 'rgba(0, 0, 0, 0.54)',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        color: 'rgba(26, 26, 26, 0.87)',
        '&:hover': {
          backgroundColor: 'rgba(0, 85, 149, 0.08)',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(0, 85, 149, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(0, 85, 149, 0.18)',
          },
        },
      },
    },
  },
  // Checkbox Styles
  MuiCheckbox: {
    styleOverrides: {
      root: {
        borderRadius: 4,
      },
    },
  },
  // Switch Styles
  MuiSwitch: {
    styleOverrides: {
      root: {
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: BRAND.primary,
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
          backgroundColor: BRAND.primary,
        },
      },
    },
  },
};

/**
 * Create Light Theme
 */
export const lightTheme = createTheme({
  palette: lightPalette,
  typography,
  components: lightComponents,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0, 85, 149, 0.06)',
    '0 2px 4px rgba(0, 85, 149, 0.10)',
    '0 4px 6px rgba(0, 85, 149, 0.12)',
    '0 6px 8px rgba(0, 85, 149, 0.14)',
    '0 8px 10px rgba(0, 85, 149, 0.15)',
    '0 10px 12px rgba(0, 85, 149, 0.16)',
    '0 12px 14px rgba(0, 85, 149, 0.17)',
    '0 14px 16px rgba(0, 85, 149, 0.18)',
    '0 16px 18px rgba(0, 85, 149, 0.19)',
    '0 18px 20px rgba(0, 85, 149, 0.20)',
    '0 20px 22px rgba(0, 85, 149, 0.22)',
    '0 22px 24px rgba(0, 85, 149, 0.24)',
    '0 24px 26px rgba(0, 85, 149, 0.26)',
    '0 26px 28px rgba(0, 85, 149, 0.28)',
    '0 28px 30px rgba(0, 85, 149, 0.30)',
    '0 30px 32px rgba(0, 85, 149, 0.32)',
    '0 32px 34px rgba(0, 85, 149, 0.34)',
    '0 34px 36px rgba(0, 85, 149, 0.36)',
    '0 36px 38px rgba(0, 85, 149, 0.38)',
    '0 38px 40px rgba(0, 85, 149, 0.40)',
    '0 40px 42px rgba(0, 85, 149, 0.42)',
    '0 42px 44px rgba(0, 85, 149, 0.44)',
    '0 44px 46px rgba(0, 85, 149, 0.46)',
    '0 46px 48px rgba(0, 85, 149, 0.48)',
  ],
});

/**
 * Create Dark Theme
 */
export const darkTheme = createTheme({
  palette: darkPalette,
  typography,
  components: darkComponents,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.2)',
    '0 2px 4px rgba(0, 0, 0, 0.3)',
    '0 4px 6px rgba(0, 0, 0, 0.35)',
    '0 6px 8px rgba(0, 0, 0, 0.4)',
    '0 8px 10px rgba(0, 0, 0, 0.45)',
    '0 10px 12px rgba(0, 0, 0, 0.5)',
    '0 12px 14px rgba(0, 0, 0, 0.55)',
    '0 14px 16px rgba(0, 0, 0, 0.6)',
    '0 16px 18px rgba(0, 0, 0, 0.65)',
    '0 18px 20px rgba(0, 0, 0, 0.7)',
    '0 20px 22px rgba(0, 0, 0, 0.75)',
    '0 22px 24px rgba(0, 0, 0, 0.8)',
    '0 24px 26px rgba(0, 0, 0, 0.85)',
    '0 26px 28px rgba(0, 0, 0, 0.9)',
    '0 28px 30px rgba(0, 0, 0, 0.95)',
    '0 30px 32px rgba(0, 0, 0, 1)',
    '0 32px 34px rgba(0, 0, 0, 1)',
    '0 34px 36px rgba(0, 0, 0, 1)',
    '0 36px 38px rgba(0, 0, 0, 1)',
    '0 38px 40px rgba(0, 0, 0, 1)',
    '0 40px 42px rgba(0, 0, 0, 1)',
    '0 42px 44px rgba(0, 0, 0, 1)',
    '0 44px 46px rgba(0, 0, 0, 1)',
    '0 46px 48px rgba(0, 0, 0, 1)',
  ],
});

/**
 * Theme Provider Props Helper
 */
export const themeProps = {
  themeMode: 'light', // 'light' | 'dark' (system mode NOT supported - never auto-detect)
};

export default lightTheme;
